import { Component, OnInit } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { NgForOf, UpperCasePipe } from '@angular/common';
import { VariableService } from '../../../../../shared/ontology/service/variable.service';
import { VariableFilterRequest } from '../../../../../shared/ontology/model/variable-filter-request';
import { StudyVariableSelectModalComponent } from '../../../../shared/modals/variable-select-modal/study-variable-select-modal.component';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { StudyEditorContext } from '../../../../study-editor.context';
import { ImportDesignContext } from '../import-design-context';
import { VariableTypeEnum } from '../../../../../shared/ontology/variable-type.enum';
import { ENTRY_NO_CVTERM_ID, PLOT_NO_CVTERM_ID, TRIAL_INSTANCE_CVTERM_ID } from '../../../../../app.constants';
import { MapItem } from '../../experiment-design.service';
import { VariableDetails } from '../../../../../shared/ontology/model/variable-details';
import { StudyInstanceService } from '../../../../../shared/study/service/study-instance.service';
import { ReviewDesignModalComponent } from '../review-design-modal/review-design-modal.component';

@Component({
    selector: 'jhi-import-design',
    templateUrl: './import-design-map.component.html',
    styleUrls: ['./import-design-map.component.scss']
})
export class ImportDesignMapComponent implements OnInit {

    public readonly REQUIRED_VARIABLES_FOR_IMPORT = [TRIAL_INSTANCE_CVTERM_ID, PLOT_NO_CVTERM_ID, ENTRY_NO_CVTERM_ID];

    unmappedItems: MapItem[] = [];
    environmentalFactors: MapItem[] = [];
    treatmentFactors: MapItem[] = [];
    designFactors: MapItem[] = [];
    germplasmFactors: MapItem[] = [];
    traits: MapItem[] = [];
    entryDetails: MapItem[] = [];

    maintainHeaders = false;
    groupName: string;
    isProcessingMapping = false;
    private existingTrialInstanceNumbers: number[];



    constructor(private studyEditorContext: StudyEditorContext,
                private variableService: VariableService,
                private importDesignContext: ImportDesignContext,
                private modalService: NgbModal,
                private activeModal: NgbActiveModal,
                private alertService: AlertService,
                private translateService: TranslateService,
                private studyInstanceService: StudyInstanceService) {

    }

    async ngOnInit(): Promise<void> {
        // Initialize with sample data
        this.unmappedItems = this.importDesignContext.unmappedItems || [];

        this.environmentalFactors = [];
        this.treatmentFactors = [];
        this.designFactors = [];
        this.germplasmFactors = [];
        this.traits = [];
        this.entryDetails = [];

        this.isProcessingMapping = true;

        try {
            // Remove LOCATION_ID and LOCATION_NAME from mapping.
            this.unmappedItems = this.unmappedItems.filter(item => item.name !== 'LOCATION_ID' && item.name !== 'LOCATION_NAME');
            let unmappedItemsTemp = [...this.unmappedItems];
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.environmentalFactors, [VariableTypeEnum.ENVIRONMENT_CONDITION, VariableTypeEnum.ENVIRONMENT_DETAIL]);
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.treatmentFactors, [VariableTypeEnum.TREATMENT_FACTOR]);
            await this.resolveTreatmentFactorPairMappings();
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.designFactors, [VariableTypeEnum.EXPERIMENTAL_DESIGN]);
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.germplasmFactors, [VariableTypeEnum.GERMPLASM_DESCRIPTOR]);
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.traits, [VariableTypeEnum.SELECTION_METHOD, VariableTypeEnum.TRAIT]);
            unmappedItemsTemp = await this.mapVariables(unmappedItemsTemp, this.entryDetails, [VariableTypeEnum.ENTRY_DETAIL]);

            this.unmappedItems = unmappedItemsTemp;
        } catch (error) {
            this.alertService.error('error.general');
        } finally {
            this.isProcessingMapping = false;
        }

        const studyInstances = await this.studyInstanceService.listStudyInstances(this.studyEditorContext.studyId).toPromise();
        this.existingTrialInstanceNumbers = studyInstances.map(studyInstance => studyInstance.instanceNumber);

        this.alertService.warning('study-editor.import-design.import-file.mapping.design.warning');
    }

    saveMapping(): void {

        if (!this.isValid()) {
            return;
        }

        if (this.unmappedItems.length > 0) {
            const modalRef = this.modalService.open(ModalConfirmComponent);
            modalRef.componentInstance.title = this.translateService.instant('study-editor.study-dataset.import-study-map.has-unmapped-headers-confirm.title');
            modalRef.componentInstance.message = this.translateService.instant('study-editor.study-dataset.import-study-map.has-unmapped-headers-confirm');
            modalRef.result.then(() => {
                this.showReviewModal();
                this.activeModal.close();
            });
        } else {
            this.showReviewModal();
            this.activeModal.close();
        }
    }

    isValid() {

        const allMappedItems = this.concatAllMappedItems();

        if (allMappedItems.length === 0) {
            this.alertService.error('study-editor.import-design.import-file.mapping.no-mapped-items');
            return false;
        }
        if (allMappedItems.length > 0) {
            const hasNoMapping = allMappedItems.some(item => item.variable === null);
            if (hasNoMapping) {
                this.alertService.error('study-editor.import-design.import-file.mapping.has-no-mapping');
                return false;
            }
        }

        const requiredVariables = allMappedItems.filter(item => {
            return item.variable && this.REQUIRED_VARIABLES_FOR_IMPORT.includes(parseInt(item.variable.id, 10));
        });
        if (requiredVariables.length !== this.REQUIRED_VARIABLES_FOR_IMPORT.length) {
            this.alertService.error('study-editor.import-design.import-file.mapping.missing.required.variables');
            return false;
        }

        // If the trial instances in the file
        // do not match the trial instances available in the study
        // just show a warning.
        const trialInstanceMapItem = this.environmentalFactors.find(item => item.variable.id === TRIAL_INSTANCE_CVTERM_ID.toString());
        if (trialInstanceMapItem && this.importDesignContext.importedData?.length) {
            const headerRow = this.importDesignContext.importedData[0]?.map(header => header.toLowerCase());
            const trialInstanceIndex = headerRow?.findIndex(header => header === trialInstanceMapItem.name.toLowerCase());
            if (trialInstanceIndex !== -1) {
                const trialInstanceNumbers = new Set<number>();
                for (const row of this.importDesignContext.importedData.slice(1)) {
                    if (row[trialInstanceIndex]) {
                        trialInstanceNumbers.add(parseInt(row[trialInstanceIndex],10));
                    }
                }
                if (trialInstanceNumbers.size !== this.existingTrialInstanceNumbers.length ||
                    ![...trialInstanceNumbers].every(num => this.existingTrialInstanceNumbers.includes(num))) {
                    this.alertService.warning('study-editor.import-design.import-file.mapping.trial-instances-do-not-match.warning');
                }
            }
        }

        return true;
    }

    showReviewModal() {
        const modalRef = this.modalService.open(ReviewDesignModalComponent as Component, { size: 'xl', backdrop: 'static' });
        modalRef.componentInstance.mappedHeader = this.concatAllMappedItems();
        modalRef.componentInstance.importedData = this.importDesignContext.importedData;
        modalRef.componentInstance.maintainHeaders = this.maintainHeaders;
    }

    concatAllMappedItems(): MapItem[] {
        return [...this.environmentalFactors, ...this.treatmentFactors, ...this.designFactors, ...this.germplasmFactors, ...this.traits, ...this.entryDetails];
    }

    private resolveVariableTypeId(variableTypeIds: VariableTypeEnum[], variable: VariableDetails): VariableTypeEnum {
        if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.ENVIRONMENT_DETAIL.valueOf())) {
            if (variable.variableTypes.some(type => type.name.toLowerCase() === 'ENVIRONMENT DETAIL'.toLowerCase())) {
                return VariableTypeEnum.ENVIRONMENT_DETAIL.valueOf();
            } else {
                return VariableTypeEnum.ENVIRONMENT_CONDITION.valueOf();
            }
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.STUDY_DETAIL.valueOf())) {
            return VariableTypeEnum.STUDY_DETAIL.valueOf();
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.TREATMENT_FACTOR.valueOf())) {
            return VariableTypeEnum.TREATMENT_FACTOR.valueOf();
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.EXPERIMENTAL_DESIGN.valueOf())) {
            return VariableTypeEnum.EXPERIMENTAL_DESIGN.valueOf();
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.GERMPLASM_DESCRIPTOR.valueOf())) {
            return VariableTypeEnum.GERMPLASM_DESCRIPTOR.valueOf();
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.ENTRY_DETAIL.valueOf())) {
            return VariableTypeEnum.ENTRY_DETAIL.valueOf();
        } else if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.TRAIT.valueOf())) {
            if (variable.variableTypes.some(type => type.name.toLowerCase() === 'TRAIT'.toLowerCase())) {
                return VariableTypeEnum.TRAIT.valueOf();
            } else {
                return VariableTypeEnum.SELECTION_METHOD.valueOf();
            }
        }
    }

    drop(event: CdkDragDrop<MapItem[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );

            // If moving to variate group, add default properties
            if (event.container.id === 'mapped-group') {
                const item = event.container.data[event.currentIndex];
                if (item) {
                    item.variable = null;
                    item.required = false;
                }
            }
        }
    }

    remapItem(item: MapItem, currentMapItems: MapItem[], variableTypeIds: VariableTypeEnum[]): void {
        const modalRef = this.modalService.open(StudyVariableSelectModalComponent as Component, {
            modalDialogClass: 'modal-lg',
            backdrop: 'static'
        });
        modalRef.componentInstance.variableTypeIds = variableTypeIds;
        modalRef.componentInstance.selectionType = 'single';
        modalRef.componentInstance.excludedVariableIds = currentMapItems.filter(mapItem => mapItem.variable).map(mapItem => parseInt(mapItem.variable.id, 10));

        const subscription = modalRef.componentInstance.variableSelect.subscribe((variable) => {
            if (variable) {
                item.variable = { id: variable.id,
                    name: variable.name,
                    property: variable.property.name,
                    scale: variable.scale.name,
                    scaleId: parseInt(variable.scale.id, 10),
                    method: variable.method.name,
                    variableTypeId: this.resolveVariableTypeId(variableTypeIds, variable),
                    dataTypeId: variable.scale.dataType.id
                };
                item.required = this.REQUIRED_VARIABLES_FOR_IMPORT.includes(parseInt(variable.id, 10));
                if (variableTypeIds.some(type => type.valueOf() === VariableTypeEnum.TREATMENT_FACTOR.valueOf())) {
                    void this.populateTreatmentFactorPairMetadata(item);
                }
            }
        });

        // Clean up when modal is closed
        modalRef.closed.subscribe(() => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
    }

    close() {
        this.activeModal.dismiss();
    }

    protected readonly VariableTypeEnum = VariableTypeEnum;

    cancel() {
        this.activeModal.dismiss();
    }

    async mapVariables(unmappedItems: MapItem[], mappedItems: MapItem[], variableTypeIds: VariableTypeEnum[]): Promise<MapItem[]> {
        const filterRequest = <VariableFilterRequest>({});
        let unmappedItemsTemp = [...unmappedItems];
        filterRequest.variableTypeIds = variableTypeIds.map((enumType) => enumType.toString());
        if (unmappedItemsTemp && unmappedItemsTemp.length) {
            filterRequest.variableNames = unmappedItemsTemp.map((mapItem) => mapItem.name);
        }
        await this.variableService.filterVariables(filterRequest).toPromise().then(variables => {
            variables.forEach(variable => {
                const existing = unmappedItemsTemp.find(item => item.name.toLowerCase() === variable.name.toLowerCase() || (variable.alias && item.name.toLowerCase().includes(variable.alias.toLowerCase())));
                if (existing) {
                    mappedItems.push({
                        id: existing.id,
                        name: existing.name,
                        variable: { id: variable.id,
                            name: variable.name,
                            property: variable.property.name,
                            scale: variable.scale.name,
                            scaleId: parseInt(variable.scale.id, 10),
                            method: variable.method.name,
                            variableTypeId: this.resolveVariableTypeId(variableTypeIds, variable),
                            dataTypeId: variable.scale.dataType.id
                        },
                        required: this.REQUIRED_VARIABLES_FOR_IMPORT.includes(parseInt(variable.id, 10)),
                    });
                    unmappedItemsTemp = unmappedItemsTemp.filter(item => item.id !== existing.id);
                }
            });
        });

        return unmappedItemsTemp;
    }

    private async resolveTreatmentFactorPairMappings(): Promise<void> {
        // Import maps treatment factor headers one by one, so we enrich each mapped item with
        // the pair metadata the backend needs to keep the treatment/value orientation intact.
        await Promise.all(this.treatmentFactors.map(item => this.populateTreatmentFactorPairMetadata(item)));
    }

    private async populateTreatmentFactorPairMetadata(item: MapItem): Promise<void> {
        if (!item.variable || item.variable.variableTypeId !== VariableTypeEnum.TREATMENT_FACTOR.valueOf()) {
            return;
        }

        const variableId = parseInt(item.variable.id, 10);
        // If the treatment factor already exists in the study, use that definition as the source
        // of truth because it matches the orientation shown in the Treatment Factors tab.
        const existingTreatmentFactor = this.studyEditorContext.studyDetails.treatmentFactors?.find(treatmentFactor =>
            treatmentFactor.levelVariable.termId === variableId || treatmentFactor.valueVariable.termId === variableId);

        if (existingTreatmentFactor) {
            item.variable.pairedVariableId = existingTreatmentFactor.levelVariable.termId === variableId
                ? existingTreatmentFactor.valueVariable.termId
                : existingTreatmentFactor.levelVariable.termId;
            item.variable.isTreatmentLabelVariable = existingTreatmentFactor.valueVariable.termId === variableId;
            return;
        }

        // For new treatment factors, rely on ontology pairs only when the paired variable is
        // also part of the current import mapping.
        const pairVariable = await this.resolveMappedTreatmentFactorPair(variableId);
        if (!pairVariable) {
            item.variable.pairedVariableId = undefined;
            item.variable.isTreatmentLabelVariable = undefined;
            return;
        }

        item.variable.pairedVariableId = pairVariable.id;
        item.variable.isTreatmentLabelVariable = this.shouldTreatAsLabelVariable(variableId, pairVariable.id);
    }

    private async resolveMappedTreatmentFactorPair(variableId: number): Promise<{ id: number } | null> {
        const variablePairs = await this.variableService.getVariablePairs(variableId).toPromise();
        if (!variablePairs?.length) {
            return null;
        }

        // Ignore ontology pairs that are not present among the headers currently mapped as
        // treatment factors, because they are not relevant for this import execution.
        const mappedTreatmentVariableIds = new Set(
            this.treatmentFactors
                .filter(mapItem => mapItem.variable)
                .map(mapItem => parseInt(mapItem.variable.id, 10))
        );

        const mappedPair = variablePairs.find(pair => mappedTreatmentVariableIds.has(parseInt(pair.id, 10)));
        return mappedPair ? { id: parseInt(mappedPair.id, 10) } : null;
    }

    private shouldTreatAsLabelVariable(variableId: number, pairedVariableId: number): boolean {
        const currentItem = this.treatmentFactors.find(mapItem => mapItem.variable?.id === variableId.toString());
        const pairedItem = this.treatmentFactors.find(mapItem => mapItem.variable?.id === pairedVariableId.toString());
        // Fallback heuristic: when only one side is numeric, treat the non-numeric variable as
        // the label companion and the numeric one as the main treatment factor.
        return pairedItem?.variable?.scale?.toLowerCase() === 'number'
            && currentItem?.variable?.scale?.toLowerCase() !== 'number';
    }
}
